
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

/** Evento usado pelo Hero para sinalizar mudança de slide. */
export const HERO_SLIDE_EVENT = 'heroSlideEvent';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicosOpen, setIsServicosOpen] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === '/';
  const hasBackground = !isHomePage || isScrolled;
  // Mobile: sempre com fundo ao rolar (mesmo na home); desktop: comportamento original
  const mobileHasBackground = isScrolled || !isHomePage;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleHomeClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const logoBgClass = hasBackground ? 'bg-[#0A1628]' : 'bg-transparent';

  const navLinks = [
    { name: 'Início', path: '/' },
    { name: 'Institucional', path: '/institucional' },
    { 
      name: 'Serviços', 
      path: '/servicos',
      hasDropdown: true,
      subLinks: [
        { name: 'Prevenção de Incêndio', path: '/servicos/prevencao-incendio' },
        { name: 'Segurança do Trabalho', path: '/servicos/seguranca-trabalho' },
        { name: 'Avaliações e Perícias', path: '/servicos/avaliacoes-pericias' },
      ]
    },
    { name: 'Treinamentos', path: '/treinamentos' },
    { name: 'Blog', path: '/blog' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contato', path: '/contato' },
  ];

  // No desktop, páginas que não são Início: sem animação de entrada, fundo imediato
  const isDesktopNonHome = !isHomePage;

  const navContent = (
    <>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`flex items-center justify-between ${isHomePage ? 'h-32 lg:h-32' : 'h-28 lg:h-32'}`}>
          <Link to="/" onClick={handleHomeClick} className="flex items-center absolute left-1/2 -translate-x-1/2 lg:static lg:left-auto lg:translate-x-0">
            <motion.div
              className={`rounded-xl px-6 py-0 lg:px-4 lg:py-2 transition-all duration-700 ${logoBgClass}`}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <img
                src="https://static.readdy.ai/image/c4e06bad978995a8efa8373b87e2590a/249d59af90f80da46e7dcb343077579c.png"
                alt="Engeporto Engenharia"
                className="w-auto object-contain h-28 lg:h-28"
              />
            </motion.div>
          </Link>

          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <div key={link.path} className="relative">
                {link.hasDropdown ? (
                  <div
                    onMouseEnter={() => setIsServicosOpen(true)}
                    onMouseLeave={() => setIsServicosOpen(false)}
                  >
                    <Link
                      to={link.path}
                      onClick={handleNavClick}
                      className="text-sm font-medium text-white hover:text-[#D4AF37] transition-colors relative whitespace-nowrap flex items-center gap-1 cursor-pointer"
                    >
                      {link.name}
                      <i className="ri-arrow-down-s-line text-base"></i>
                    </Link>
                    {isServicosOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-2xl py-2 border border-neutral-100"
                      >
                        {link.subLinks?.map((subLink) => (
                          <Link
                            key={subLink.path}
                            to={subLink.path}
                            onClick={handleNavClick}
                            className="block px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-[#D4AF37] transition-colors"
                          >
                            {subLink.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    onClick={handleNavClick}
                    className="text-sm font-medium text-white hover:text-[#D4AF37] transition-colors relative whitespace-nowrap cursor-pointer"
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Gostaria de solicitar um orçamento.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white hover:text-[#D4AF37] transition-colors cursor-pointer"
            >
              <i className="ri-phone-line text-lg"></i>
              <span className="text-sm font-medium whitespace-nowrap">(15) 99671-9308</span>
            </a>
            <a
              href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Gostaria de solicitar um orçamento.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#D4AF37] text-[#0A1628] px-6 py-2.5 rounded-md text-sm font-semibold hover:bg-[#C4A137] transition-all whitespace-nowrap cursor-pointer"
            >
              ANÁLISE TÉCNICA GRATUITA
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl text-white`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="lg:hidden bg-[#0A1628] border-t border-white/10"
        >
          <div className="px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              <div key={link.path}>
                <Link
                  to={link.path}
                  onClick={handleNavClick}
                  className="block py-2 text-sm font-medium text-white hover:text-[#D4AF37]"
                >
                  {link.name}
                </Link>
                {link.hasDropdown && link.subLinks && (
                  <div className="pl-4 space-y-2 mt-2">
                    {link.subLinks.map((subLink) => (
                      <Link
                        key={subLink.path}
                        to={subLink.path}
                        onClick={handleNavClick}
                        className="block py-2 text-sm text-neutral-300 hover:text-[#D4AF37]"
                      >
                        {subLink.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a
              href="tel:+5515996719308"
              className="block py-2 text-sm font-medium text-white hover:text-[#D4AF37] cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              (15) 99671-9308
            </a>
            <a
              href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Gostaria de solicitar um orçamento.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-[#D4AF37] text-[#0A1628] px-6 py-3 rounded-md text-sm font-semibold text-center cursor-pointer"
            >
              ANÁLISE TÉCNICA GRATUITA
            </a>
          </div>
        </motion.div>
      )}
    </>
  );

  // Página Início: com animação de entrada (motion.nav)
  if (isHomePage) {
    return (
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          mobileHasBackground ? 'bg-[#0A1628] shadow-2xl' : 'bg-transparent'
        } ${hasBackground ? 'lg:bg-[#0A1628] lg:shadow-2xl' : 'lg:bg-transparent lg:shadow-none'}`}
      >
        {navContent}
      </motion.nav>
    );
  }

  // Demais páginas (desktop): sem animação de entrada, fundo imediato, cor fixa igual ao box do logo
  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-[#0A1628] shadow-2xl"
    >
      {navContent}
    </nav>
  );
};

export default Navbar;
